// Pure outbox logic: ordering, backoff, error classification, id/revision rebasing, and the
// optimistic overlay. The IndexedDB persistence and network runner live in outbox.ts.
import type { CreateTaskInput, Destination, TaskWithFlags, UpdateTaskInput } from "@desk/contracts";

export type TaskActionKind = "complete" | "reopen" | "cancel" | "unsnooze" | "review" | "delete" | "restore";

export type OutboxOp =
  | { type: "task.create"; input: CreateTaskInput & { clientId: string } }
  | { type: "task.update"; taskId: string; patch: UpdateTaskInput }
  | { type: "task.action"; taskId: string; action: TaskActionKind }
  | { type: "task.snooze"; taskId: string; until: string }
  | { type: "task.move"; taskId: string; dest: Destination; baseRevision?: number }
  | { type: "plan.add"; date: string; taskIds: string[] }
  | { type: "plan.remove"; date: string; taskId: string }
  | { type: "checklist.add"; taskId: string; title: string; clientId: string }
  | { type: "checklist.update"; taskId: string; itemId: string; patch: { title?: string; done?: boolean } }
  | { type: "checklist.delete"; taskId: string; itemId: string }
  | { type: "checklist.reorder"; taskId: string; ids: string[] };

export type EntryState = "pending" | "sending" | "failed" | "conflict";

export interface OutboxEntry {
  /** Doubles as the Idempotency-Key. For creates it equals the task clientId. */
  id: string;
  op: OutboxOp;
  createdAt: number;
  attempts: number;
  nextAttemptAt: number;
  state: EntryState;
  error?: string;
  /** Server's current record on 409. */
  current?: unknown;
}

export interface OutboxMeta {
  /** clientId -> server task id, for entries queued before their create was acknowledged. */
  idMap: Record<string, string>;
  /** taskId -> { revisionBefore: revisionAfter } for our own successful writes. */
  revisions: Record<string, Record<string, number>>;
}

export const emptyMeta = (): OutboxMeta => ({ idMap: {}, revisions: {} });

export function opTaskIds(op: OutboxOp): string[] {
  switch (op.type) {
    case "task.create":
      return [op.input.clientId];
    case "plan.add":
      return op.taskIds;
    default:
      return [op.taskId];
  }
}

/** Exponential backoff: 1s, 2s, 4s … capped at 60s, with optional jitter (0..1). */
export function backoffMs(attempts: number, jitter = 0): number {
  const base = Math.min(60_000, 1000 * 2 ** Math.max(0, attempts - 1));
  return Math.round(base * (1 + jitter * 0.25));
}

export type Outcome = "retry" | "conflict" | "failed";
export function classify(err: { status?: number; code?: string } | undefined): Outcome {
  if (!err) return "retry";
  if (err.code === "network" || err.status === 0 || err.status === undefined) return "retry";
  if (err.status === 429 || (err.status ?? 0) >= 500) return "retry";
  if (err.status === 409 && err.code === "conflict") return "conflict";
  return "failed";
}

export function resolveId(id: string, meta: OutboxMeta): string {
  return meta.idMap[id] ?? id;
}

/** Follow our own revision chain so queued edits don't conflict with our earlier writes. */
export function rebaseRevision(taskId: string, base: number | undefined, meta: OutboxMeta): number | undefined {
  if (base === undefined) return undefined;
  const chain = meta.revisions[taskId];
  if (!chain) return base;
  let b = base;
  for (let guard = 0; guard < 1000 && chain[String(b)] !== undefined; guard++) b = chain[String(b)]!;
  return b;
}

/** Record that our own write moved `taskId` to `revision` (from revision - 1). */
export function recordRevision(meta: OutboxMeta, taskId: string, revision: number | undefined): OutboxMeta {
  if (!revision || revision < 2) return meta;
  const chain = { ...(meta.revisions[taskId] ?? {}), [String(revision - 1)]: revision };
  return { ...meta, revisions: { ...meta.revisions, [taskId]: chain } };
}

/**
 * Pick the next entry to send. Entries are processed in creation order. An entry is blocked when
 * an earlier entry touching the same task is still unsent, failed, or in conflict — later edits
 * must not overtake (or be applied without) earlier ones. Entries for other tasks continue.
 */
export function nextRunnable(entries: OutboxEntry[], now: number): OutboxEntry | null {
  const sorted = [...entries].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  const blocked = new Set<string>();
  for (const e of sorted) {
    const ids = opTaskIds(e.op);
    const isBlocked = ids.some((id) => blocked.has(id));
    if (e.state === "pending" && !isBlocked && e.nextAttemptAt <= now) return e;
    // Anything not yet acknowledged blocks later entries for the same task(s).
    ids.forEach((id) => blocked.add(id));
  }
  return null;
}

/** Earliest time a pending, unblocked-by-time entry becomes due (for scheduling a timer). */
export function nextWake(entries: OutboxEntry[]): number | null {
  let min: number | null = null;
  for (const e of entries) if (e.state === "pending" && (min === null || e.nextAttemptAt < min)) min = e.nextAttemptAt;
  return min;
}

export type SyncMark = "pending" | "failed" | "conflict";
export interface Overlay {
  /** Field patches keyed by task id (server or client id). */
  patches: Map<string, Partial<TaskWithFlags>>;
  /** Sync state per task id, for row indicators. */
  marks: Map<string, SyncMark>;
  /** Tasks created locally and not yet acknowledged. */
  creates: TaskWithFlags[];
  /** Plan membership changes not yet acknowledged: date -> taskId -> added(true)/removed(false). */
  plan: Map<string, Map<string, boolean>>;
}

function markOf(state: EntryState): SyncMark {
  return state === "failed" ? "failed" : state === "conflict" ? "conflict" : "pending";
}

const MARK_RANK: Record<SyncMark, number> = { pending: 0, failed: 1, conflict: 2 };

/** Build the optimistic overlay from unacknowledged entries. */
export function buildOverlay(entries: OutboxEntry[], meta: OutboxMeta, nowIso: string): Overlay {
  const patches = new Map<string, Partial<TaskWithFlags>>();
  const marks = new Map<string, SyncMark>();
  const creates: TaskWithFlags[] = [];
  const plan = new Map<string, Map<string, boolean>>();
  const sorted = [...entries].sort((a, b) => a.createdAt - b.createdAt);
  const patch = (id: string, p: Partial<TaskWithFlags>) => patches.set(id, { ...(patches.get(id) ?? {}), ...p });
  const mark = (id: string, m: SyncMark) => {
    const prev = marks.get(id);
    if (!prev || MARK_RANK[m] > MARK_RANK[prev]) marks.set(id, m);
  };
  for (const e of sorted) {
    const op = e.op;
    const m = markOf(e.state);
    if (op.type === "task.create") {
      const i = op.input;
      const id = resolveId(i.clientId, meta);
      if (id !== i.clientId) continue; // acknowledged; server row will appear
      creates.push({
        id: i.clientId,
        readableId: "DSK-0",
        title: i.title,
        description: i.description ?? "",
        areaId: i.areaId ?? null,
        spaceId: i.spaceId ?? null,
        projectId: i.projectId ?? null,
        status: i.status ?? "todo",
        priority: i.priority ?? "none",
        plannedDate: i.planDate ?? null,
        deadline: i.deadline ?? null,
        reminderAt: i.reminderAt ?? null,
        snoozedUntil: null,
        labels: i.labels ?? [],
        checklist: [],
        position: Number.MAX_SAFE_INTEGER,
        createdAt: new Date(e.createdAt).toISOString(),
        updatedAt: new Date(e.createdAt).toISOString(),
        completedAt: null,
        lastActivityAt: new Date(e.createdAt).toISOString(),
        deletedAt: null,
        revision: 1,
        repeat: i.repeat ?? null,
        nextOccurrenceId: null,
        flags: { isSnoozed: false, isOverdue: false, isDueToday: false, inSettledContainer: false },
      });
      mark(i.clientId, m);
      continue;
    }
    if (op.type === "plan.add" || op.type === "plan.remove") {
      const day = plan.get(op.date) ?? new Map<string, boolean>();
      if (op.type === "plan.add") op.taskIds.forEach((t) => day.set(resolveId(t, meta), true));
      else day.set(resolveId(op.taskId, meta), false);
      plan.set(op.date, day);
      continue;
    }
    const id = resolveId(op.taskId, meta);
    mark(id, m);
    switch (op.type) {
      case "task.update": {
        const { baseRevision: _b, ...fields } = op.patch;
        void _b;
        patch(id, fields as Partial<TaskWithFlags>);
        break;
      }
      case "task.action":
        if (op.action === "complete") patch(id, { status: "done", completedAt: nowIso });
        else if (op.action === "reopen") patch(id, { status: "todo", completedAt: null });
        else if (op.action === "cancel") patch(id, { status: "cancelled" });
        else if (op.action === "unsnooze") patch(id, { snoozedUntil: null });
        else if (op.action === "delete") patch(id, { deletedAt: nowIso });
        else if (op.action === "restore") patch(id, { deletedAt: null });
        break;
      case "task.snooze":
        patch(id, { snoozedUntil: op.until });
        break;
      case "task.move":
        patch(id, {
          areaId: op.dest.areaId ?? null,
          spaceId: op.dest.spaceId ?? null,
          projectId: op.dest.projectId ?? null,
        });
        break;
      default:
        break;
    }
  }
  // Key creates' patches by their client id too, so edits to unsynced tasks show immediately.
  for (const c of creates) {
    const p = patches.get(c.id);
    if (p) Object.assign(c, p);
  }
  return { patches, marks, creates, plan };
}

/** Apply overlay patches to server rows. Rows with a pending delete are dropped. */
export function applyPatches<T extends TaskWithFlags>(tasks: T[], overlay: Overlay): T[] {
  if (overlay.patches.size === 0) return tasks;
  const out: T[] = [];
  for (const t of tasks) {
    const p = overlay.patches.get(t.id);
    if (!p) {
      out.push(t);
      continue;
    }
    const merged = { ...t, ...p } as T;
    if ("snoozedUntil" in p) merged.flags = { ...merged.flags, isSnoozed: !!p.snoozedUntil && p.snoozedUntil > new Date().toISOString() };
    if (merged.deletedAt && !t.deletedAt) continue;
    out.push(merged);
  }
  return out;
}

export function applyPatch<T extends TaskWithFlags>(task: T, overlay: Overlay): T {
  const p = overlay.patches.get(task.id);
  return p ? ({ ...task, ...p } as T) : task;
}
